import semver from "semver";
import { fetchMinVersion } from "@ano/shared/api/min-version";

export interface MinSkillVersionViolation {
  currentVersion: string;
  minVersion: string;
  upgradeCommand: string;
}

export interface MinSkillVersionCheckInput {
  apiBaseUrl: string;
  currentVersion: string | null;
}

const DEFAULT_SKILL_UPGRADE_COMMAND =
  "claude plugin marketplace add ano-chat/ano-skills && claude plugin install ano-skills@ano-skills";

/**
 * Fetches `/api/min-version` and compares against the locally installed
 * `@ano-chat/skills` Claude Code plugin version. Sibling of
 * {@link import("./min-cli-version-check").checkMinCliVersion} — same
 * shape, different artifact. The skills/commands (`/unread`, ano-messages,
 * …) ship via the plugin marketplace, a channel separate from the `ano`
 * CLI binary, so the two nudges are independent.
 *
 * Fails open on any error (network, 5xx, malformed response) so a server
 * hiccup never blocks the app.
 *
 * `currentVersion === null` means the plugin isn't installed (or Claude
 * Code isn't installed) — surfaced as a violation so the toast can prompt
 * installation, but only when `minSkillVersion > 0.0.0`.
 */
export async function checkMinSkillVersion({
  apiBaseUrl,
  currentVersion,
}: MinSkillVersionCheckInput): Promise<MinSkillVersionViolation | null> {
  try {
    const response = await fetchMinVersion({ apiBaseUrl });
    const min = response.minSkillVersion;
    if (!semver.valid(min) || semver.eq(min, "0.0.0")) {
      // Min not enforced — nudge stays dark.
      return null;
    }
    const upgradeCommand =
      response.skillUpgradeCommand?.trim() || DEFAULT_SKILL_UPGRADE_COMMAND;
    if (currentVersion === null) {
      return {
        currentVersion: "(not installed)",
        minVersion: min,
        upgradeCommand,
      };
    }
    if (!semver.valid(currentVersion)) {
      // Unrecognizable version (e.g. a git-sha plugin install) — fail open
      // rather than nag about a version we can't compare.
      return null;
    }
    if (semver.lt(currentVersion, min)) {
      return {
        currentVersion,
        minVersion: min,
        upgradeCommand,
      };
    }
    return null;
  } catch (error) {
    console.warn("[min-skill-version] check failed — failing open", error);
    return null;
  }
}
